<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { storeToRefs } from 'pinia'
import Toaster from '@/components/common/Toaster.vue'
import GlobalSearchModal from '@/components/common/GlobalSearchModal.vue'
import AudioMiniBar from '@/components/common/AudioMiniBar.vue'
import { useOnlineStatus } from '@/composables/useOnlineStatus'
import { useGlobalSearchModal } from '@/composables/useGlobalSearchModal'
import { useAuthStore } from '@/sync/auth'
import { useSettingsStore } from '@/stores/settings'
import { useShelvesStore } from '@/stores/shelves'
import { useReviewsStore } from '@/stores/reviews'
import { useFavoritesStore } from '@/stores/favorites'
import { useLibraryStore } from '@/stores/library'
import { useNotificationsStore } from '@/stores/notifications'
import { useShelvesSync } from '@/sync/useShelvesSync'
import { useReviewsSync } from '@/sync/useReviewsSync'
import { useFavoritesSync } from '@/sync/useFavoritesSync'
import { useAiSettingsSync } from '@/sync/useAiSettingsSync'

// Versión inyectada por Vite (define) para diagnóstico de caché SW.
declare const __APP_VERSION__: string
const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'

const route = useRoute()
const { online } = useOnlineStatus()

// Cerrar el menú móvil al navegar a otra ruta.
watch(() => route.path, () => closeMobileNav())

// El lector ocupa pantalla completa; oculta la barra superior.
const isFullscreen = computed(() => Boolean(route.meta.fullscreen))

// --- Búsqueda global ---
const { open: searchOpen, toggle: toggleSearch } = useGlobalSearchModal()

// --- Menú de navegación móvil (hamburguesa) ---
const mobileNavOpen = ref(false)
function closeMobileNav() {
  mobileNavOpen.value = false
}
function onMobileNavKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && mobileNavOpen.value) closeMobileNav()
}

function onGlobalShortcut(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    toggleSearch()
  }
}

// Sincronización de estanterías: doc CRDT global por usuario, abierto
// mientras haya sesión activa. Se cierra automáticamente al cerrar sesión.
const auth = useAuthStore()
const settingsStore = useSettingsStore()
const shelvesStore = useShelvesStore()
const reviewsStore = useReviewsStore()
const favoritesStore = useFavoritesStore()
const libraryStore = useLibraryStore()
const notifications = useNotificationsStore()

/** ¿Sync activo + sesión? Controla la visibilidad del enlace «En la nube». */
const syncReady = computed(
  () => settingsStore.settings.sync.enabled && auth.isAuthenticated,
)
const { shelves: shelvesRef } = storeToRefs(shelvesStore)
const { cards: reviewCardsRef } = storeToRefs(reviewsStore)
const { favorites: favoritesRef } = storeToRefs(favoritesStore)
const { openShelvesSync, closeShelvesSync } = useShelvesSync()
const { openReviewsSync, closeReviewsSync } = useReviewsSync()
const { openFavoritesSync, closeFavoritesSync } = useFavoritesSync()

watch(
  () => auth.isAuthenticated,
  async (isAuth) => {
    if (isAuth) {
      // Asegurar que las estanterías, tarjetas y favoritos locales estén
      // cargados antes de conectar el CRDT, para que la reconciliación
      // inicial sea correcta.
      if (shelvesStore.shelves.length === 0) await shelvesStore.loadAll()
      if (reviewsStore.cards.length === 0) await reviewsStore.loadAll()
      if (favoritesStore.favorites.length === 0) await favoritesStore.loadAll()
      openShelvesSync(shelvesRef)
      openReviewsSync(reviewCardsRef)
      openFavoritesSync(favoritesRef)
      // Sembrar el CRDT de favoritos con los favoritos locales pre-CRDT
      // (migración suave, idempotente). Fire-and-forget.
      void favoritesStore.seedFromLocalBooks(
        libraryStore.books.filter((b) => b.favorite).map((b) => b.id),
      )
      // Notificaciones (F2.4): carga inicial + suscripción Realtime.
      void notifications.load()
    } else {
      closeShelvesSync()
      closeReviewsSync()
      closeFavoritesSync()
      notifications.$reset()
    }
  },
  { immediate: true },
)

// --- Sync cifrada de la config IA (cuando la clave está desbloqueada) ---
const { pullEncryptedSettings, pushEncryptedSettings } = useAiSettingsSync()
let aiPushTimer: ReturnType<typeof setTimeout> | null = null
let aiWatchStop: (() => void) | null = null

/**
 * Cuando el usuario desbloquea (login caliente o unlock perezoso), hacemos
 * un pull inicial (nube gana) y montamos un watcher con debounce que empuja
 * los cambios locales. Si la clave se bloquea/cierra sesión, lo desmontamos.
 */
watch(
  () => auth.isKeyUnlocked,
  async (unlocked) => {
    if (unlocked && auth.isAuthenticated) {
      // Pull inicial: la nube puede tener una versión más reciente.
      await pullEncryptedSettings()
      // Montar el watcher de push si no lo está ya.
      if (!aiWatchStop) {
        aiWatchStop = watch(
          // Huella de la config IA SIN `lastSyncedAt`. El propio push escribe
          // `lastSyncedAt` (vía updateAiLastSyncedAt); si observáramos `ai`
          // completo con deep, esa mutación re-dispararía el watcher → otro
          // push → bucle infinito. Comparando por valor (JSON.stringify) y
          // excluyendo `lastSyncedAt`, el watcher sólo se dispara ante
          // cambios REALES de la config (apiKey, model, routing, etc.).
          () => JSON.stringify({ ...settingsStore.settings.ai, lastSyncedAt: undefined }),
          () => {
            // Ignorar cambios que vienen de un pull remoto (evita bucle).
            if (settingsStore.isApplyingRemoteAi()) return
            if (aiPushTimer) clearTimeout(aiPushTimer)
            aiPushTimer = setTimeout(() => void pushEncryptedSettings(), 1500)
          },
        )
      }
    } else if (aiWatchStop) {
      aiWatchStop()
      aiWatchStop = null
      if (aiPushTimer) clearTimeout(aiPushTimer)
    }
  },
  { immediate: true },
)

// Cargar siempre las tarjetas de repaso al arrancar (incluso sin sync),
// para que la vista de repaso y el badge funcionen offline.
onMounted(() => {
  void reviewsStore.loadAll()
})

// Registrar el atajo Cmd/Ctrl+K.
// El índice de búsqueda global YA NO se construye al arrancar: con muchos
// libros, `searchStore.init()` lee la tabla `bookIndex` entera (decenas de
// miles de chunks) y los mete en FlexSearch de forma síncrona, congelando el
// primer render aunque se difiera a idle. Ahora se inicializa la primera vez
// que se abre el modal de búsqueda (ver GlobalSearchModal.vue).
onMounted(() => {
  window.addEventListener('keydown', onGlobalShortcut)
  window.addEventListener('keydown', onMobileNavKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalShortcut)
  window.removeEventListener('keydown', onMobileNavKeydown)
})
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Saltar al contenido (accesibilidad: usuarios de teclado/lector) -->
    <a
      href="#contenido-principal"
      class="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[60] focus:rounded-lg focus:bg-accent focus:px-4 focus:py-2 focus:text-sm focus:text-accent-fg"
    >
      {{ $t('nav.skipToContent') }}
    </a>

    <!-- Banner de sin conexión -->
    <Transition name="slide-down">
      <div
        v-if="!online"
        class="flex items-center justify-center gap-2 bg-accent px-4 py-1.5 text-center text-xs font-medium text-accent-fg"
        role="status"
      >
        <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4M12 16h.01M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
        </svg>
        {{ $t('sync.offlineBanner') }}
      </div>
    </Transition>

    <header
      v-if="!isFullscreen"
      role="banner"
      class="flex items-center gap-1 border-b border-surface-muted/60 bg-surface-subtle/70 px-4 py-3 backdrop-blur"
    >
      <RouterLink to="/library" class="flex items-center gap-2 text-ink">
        <img src="/favicon.svg" alt="" class="h-7 w-7" />
        <span class="hidden font-display text-lg font-medium tracking-tight lg:inline">EPUB Unlimited</span>
      </RouterLink>

      <!-- Navegación horizontal en desktop (≥1024px). En tablet/móvil se usa
           el drawer hamburguesa para evitar scroll horizontal con tantos
           enlaces (10+) que no caben en pantallas de ~712px. -->
      <nav aria-label="Navegación principal" class="ml-6 hidden items-center gap-0.5 text-sm lg:flex">
        <RouterLink
          to="/library"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.library') }}
        </RouterLink>
        <RouterLink
          to="/importar"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.import') }}
        </RouterLink>
        <RouterLink
          v-if="syncReady"
          to="/nube"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.cloud') }}
        </RouterLink>
        <RouterLink
          to="/settings"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.settings') }}
        </RouterLink>
        <RouterLink
          to="/stats"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.stats') }}
        </RouterLink>
        <RouterLink
          v-if="auth.isAdmin"
          to="/admin"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          <span class="mr-1">🛡️</span>{{ $t('admin.title') }}
        </RouterLink>
        <RouterLink
          to="/conocimiento"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.knowledge') }}
        </RouterLink>
        <RouterLink
          to="/repaso"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.review') }}
          <span
            v-if="reviewsStore.dueCount > 0"
            class="ml-1 rounded-full bg-accent px-1.5 py-0.5 text-[0.6rem] font-semibold text-accent-fg"
          >
            {{ reviewsStore.dueCount }}
          </span>
        </RouterLink>
        <RouterLink
          to="/descubrir"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.discover') }}
        </RouterLink>
        <RouterLink
          v-if="notifications.unreadCount > 0 || notifications.items.length > 0"
          to="/notificaciones"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
          :title="$t('nav.notifications')"
        >
          <span>🔔</span>
          <span
            v-if="notifications.unreadCount > 0"
            class="ml-1 rounded-full bg-accent px-1.5 py-0.5 text-[0.6rem] font-semibold text-accent-fg"
          >
            {{ notifications.unreadCount }}
          </span>
        </RouterLink>
        <RouterLink
          to="/auth"
          class="nav-link relative px-3 py-1.5 text-ink-subtle transition hover:text-ink"
          active-class="is-active text-ink"
        >
          {{ $t('nav.account') }}
        </RouterLink>
      </nav>

      <!-- Acciones a la derecha: búsqueda (siempre) + hamburguesa (tablet/móvil) -->
      <div class="ml-auto flex items-center gap-1 lg:ml-6">
        <button
          type="button"
          class="rounded-lg p-2 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink lg:hidden"
          :title="$t('nav.search')"
          :aria-label="$t('nav.search')"
          @click="toggleSearch"
        >
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="11" cy="11" r="7" />
            <path stroke-linecap="round" d="M21 21l-4-4" />
          </svg>
        </button>
        <button
          type="button"
          class="rounded-lg p-2 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink lg:hidden"
          :title="mobileNavOpen ? $t('nav.closeMenu') : $t('nav.openMenu')"
          :aria-label="mobileNavOpen ? $t('nav.closeMenu') : $t('nav.openMenu')"
          :aria-expanded="mobileNavOpen"
          aria-controls="mobile-nav-drawer"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <svg v-if="!mobileNavOpen" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 7h16M4 12h16M4 17h16" />
          </svg>
          <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
    </header>

    <!-- Drawer de navegación móvil: panel desplegable bajo el header.
         Se cierra al navegar (watch route) o con Escape. -->
    <Transition name="mobile-nav">
      <nav
        v-if="mobileNavOpen"
        id="mobile-nav-drawer"
        aria-label="Navegación móvil"
        class="flex flex-col gap-0.5 border-b border-surface-muted/60 bg-surface-subtle/95 px-3 pb-4 pt-2 text-sm shadow-lg backdrop-blur lg:hidden"
      >
        <RouterLink to="/library" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          📚 {{ $t('nav.library') }}
        </RouterLink>
        <RouterLink to="/importar" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          ⬇️ {{ $t('nav.import') }}
        </RouterLink>
        <RouterLink v-if="syncReady" to="/nube" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          ☁️ {{ $t('nav.cloud') }}
        </RouterLink>
        <RouterLink to="/settings" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          ⚙️ {{ $t('nav.settings') }}
        </RouterLink>
        <RouterLink to="/stats" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          📊 {{ $t('nav.stats') }}
        </RouterLink>
        <RouterLink v-if="auth.isAdmin" to="/admin" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          🛡️ {{ $t('admin.title') }}
        </RouterLink>
        <RouterLink to="/conocimiento" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          💡 {{ $t('nav.knowledge') }}
        </RouterLink>
        <RouterLink to="/repaso" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          🔁 {{ $t('nav.review') }}
          <span v-if="reviewsStore.dueCount > 0" class="ml-1 rounded-full bg-accent px-1.5 py-0.5 text-[0.6rem] font-semibold text-accent-fg">
            {{ reviewsStore.dueCount }}
          </span>
        </RouterLink>
        <RouterLink to="/descubrir" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          🔍 {{ $t('nav.discover') }}
        </RouterLink>
        <RouterLink
          v-if="notifications.unreadCount > 0 || notifications.items.length > 0"
          to="/notificaciones"
          class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink"
          active-class="bg-accent/10 font-medium text-ink"
        >
          🔔 {{ $t('nav.notifications') }}
          <span v-if="notifications.unreadCount > 0" class="ml-1 rounded-full bg-accent px-1.5 py-0.5 text-[0.6rem] font-semibold text-accent-fg">
            {{ notifications.unreadCount }}
          </span>
        </RouterLink>
        <RouterLink to="/auth" class="rounded-lg px-3 py-2.5 text-ink-subtle transition hover:bg-surface-muted/60 hover:text-ink" active-class="bg-accent/10 font-medium text-ink">
          👤 {{ $t('nav.account') }}
        </RouterLink>
      </nav>
    </Transition>

    <main id="contenido-principal" role="main" tabindex="-1" class="flex-1 overflow-hidden focus:outline-none">
      <RouterView v-slot="{ Component }">
        <Transition name="fade" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </main>

    <Toaster />

    <!-- Reproductor de audiolibro (global: sobrevive a la navegación) -->
    <AudioMiniBar />

    <!-- Búsqueda global (Cmd/Ctrl+K) -->
    <GlobalSearchModal v-model:open="searchOpen" />

    <!-- Indicador de versión para diagnóstico de caché SW (temporal) -->
    <div
      style="position: fixed; bottom: 4px; right: 6px; z-index: 9999; font-size: 9px; color: rgba(128,128,128,0.5); pointer-events: none; font-family: monospace;"
    >
      v{{ appVersion }}
    </div>
  </div>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-down-enter-active,
.slide-down-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

/*
 * Drawer de navegación móvil: despliegue vertical suave desde el header.
 */
.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/*
 * Subrayado dorado editorial bajo el enlace de navegación activo.
 * Se anima de izquierda a derecha al activarse.
 */
.nav-link.is-active::after {
  content: '';
  position: absolute;
  left: 0.75rem;
  right: 0.75rem;
  bottom: 0.15rem;
  height: 1.5px;
  background: rgb(var(--accent));
  border-radius: 9999px;
  animation: nav-underline 0.25s ease;
}
@keyframes nav-underline {
  from {
    transform: scaleX(0);
    transform-origin: left;
  }
  to {
    transform: scaleX(1);
    transform-origin: left;
  }
}
</style>
